<template>
  <div id="black-royalty-report-admin">
    <vx-card ref="tabsWrapper">
      <div class="vx-row mb-4">
        <div class="vx-col md:w-1/2 w-full mt-2">
          <label>Diesel Rate (R/L)</label>
          <vs-input
            v-model.number="dieselRate"
            type="number"
            step="0.01"
            class="w-full"
          />
        </div>
        <div class="vx-col md:w-1/2 w-full mt-2">
          <label>Month</label>
          <input type="month" v-model="month" class="vs-input w-full" />
        </div>
      </div>
      <div class="vx-col w-full mb-base">
        <vs-button
          class="rounded-lg w-full"
          icon-pack="feather"
          icon="icon-download"
          :disabled="exporting"
          @click="exportAll"
        >
          {{ exporting ? "Exporting..." : "Export All" }}
        </vs-button>
      </div>

      <vs-tabs v-model="activeTab" @input="onTabChange">
        <vs-tab
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :label="tab.label"
          style="min-height: auto"
        >
          <div class="pt-4">
            <h4 class="mb-4">{{ tab.label }}</h4>

            <div class="flex items-center mb-4" v-if="tab.key === 'weekDetail'">
              <vs-select label="Location" v-model="weekDetailLocation" class="mr-4">
                <vs-select-item text="Pit" value="Pit" />
                <vs-select-item text="Dump" value="Dump" />
              </vs-select>
              <vs-select label="Week" v-model="weekDetailWeek">
                <vs-select-item v-for="w in 5" :key="w" :text="'Week ' + w" :value="w" />
              </vs-select>
            </div>

            <vs-button
              size="small"
              type="border"
              :disabled="loading[tab.key]"
              class="mb-4"
              @click="loadTab(tab.key)"
            >
              {{ loading[tab.key] ? "Loading..." : "Refresh" }}
            </vs-button>

            <dynamic-result-table
              v-if="activeTab === index"
              :rows="results[tab.key]"
            />
          </div>
        </vs-tab>
      </vs-tabs>
    </vx-card>
  </div>
</template>

<script>
import DynamicResultTable from "./components/DynamicResultTable.vue";

const TABS = [
  { key: "dashboardKpi", label: "Dashboard KPI Summary" },
  { key: "weeklySummary", label: "Weekly Summary" },
  { key: "monthlyByGroup", label: "Monthly By Group" },
  { key: "oem", label: "OEM Vs Consumption" },
  { key: "pitMatrix", label: "Pit Consumption" },
  { key: "dumpMatrix", label: "Dump Consumption" },
  { key: "weekDetail", label: "Week Detail" },
  { key: "deliveredKpi", label: "Diesel Delivered KPI" },
  { key: "deliveredDetail", label: "Diesel Delivered Detail" }
];

export default {
  name: "BlackRoyaltyReportAdmin",
  components: { DynamicResultTable },
  data() {
    return {
      accountId: 262,
      dieselRate: 25.24,
      month: new Date().toISOString().slice(0, 7), // YYYY-MM
      activeTab: 0,
      exporting: false,
      weekDetailLocation: "Pit",
      weekDetailWeek: 1,
      tabs: TABS,
      loading: {
        dashboardKpi: false,
        weeklySummary: false,
        monthlyByGroup: false,
        oem: false,
        pitMatrix: false,
        dumpMatrix: false,
        weekDetail: false,
        deliveredKpi: false,
        deliveredDetail: false
      },
      results: {
        dashboardKpi: [],
        weeklySummary: [],
        monthlyByGroup: [],
        oem: [],
        pitMatrix: [],
        dumpMatrix: [],
        weekDetail: [],
        deliveredKpi: [],
        deliveredDetail: []
      }
    };
  },
  watch: {
    weekDetailLocation() {
      this.loadTab("weekDetail");
    },
    weekDetailWeek() {
      this.loadTab("weekDetail");
    },
    month() {
      this.loadCurrentDieselRate();
    }
  },
  mounted() {
    this.loadCurrentDieselRate();
    this.loadTab(this.tabs[this.activeTab].key);
  },
  methods: {
    // Diesel Rate now only acts as a fallback (each transaction/delivery uses
    // its own historical price where available) - this just keeps the field
    // populated with a sensible current value instead of a stale hardcoded
    // default. Reuses the existing Diesel Delivered Detail endpoint (already
    // ordered oldest-to-newest) rather than a dedicated endpoint - the most
    // recent delivery's own Rate (R/L) is the current price.
    loadCurrentDieselRate() {
      var self = this;
      self.$ajaxGet(
        self,
        `BlackRoyalty/diesel-delivered-detail?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
        function (response) {
          var rows = response.data || [];
          if (rows.length > 0) {
            var latest = rows[rows.length - 1];
            if (latest["Rate (R/L)"] != null) {
              self.dieselRate = latest["Rate (R/L)"];
            }
          }
        },
        function () {}
      );
    },
    // Same helper used across the other FAMSUI pages (e.g. Equipment.vue) -
    // Vuesax's $vs.loading overlay pinned to a specific ref's element.
    showLoadingOnElement(element, scale, type) {
      var self = this;
      self.$vs.loading({
        type: type,
        container: self.$refs[element].$el,
        scale: scale
      });
    },
    monthParam() {
      return `${this.month}-01`;
    },
    onTabChange(index) {
      this.loadTab(this.tabs[index].key);
    },
    // Matches the app-wide $ajaxGet(self, url, onSuccess, onFinally) convention
    // (see main.js) used across the FAMSUI pages instead of calling axios
    // directly - it builds the URL via $buildUrl (VUE_APP_ROOT_API + path),
    // attaches the standard auth headers, and already shows an error toast
    // on failure, so no local error-handling UI is needed here.
    loadTab(key) {
      var self = this;
      self.loading[key] = true;
      self.showLoadingOnElement("tabsWrapper", 1, "radius");
      var onFinally = function () {
        self.loading[key] = false;
        self.$vs.loading.close(self.$refs.tabsWrapper.$el);
      };

      switch (key) {
        case "dashboardKpi":
          self.$ajaxGet(
            self,
            `BlackRoyalty/dashboard-kpi-summary?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.dashboardKpi = response.data ? [response.data] : [];
            },
            onFinally
          );
          break;
        case "weeklySummary":
          self.$ajaxGet(
            self,
            `BlackRoyalty/dashboard-weekly-summary?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.weeklySummary = response.data || [];
            },
            onFinally
          );
          break;
        case "monthlyByGroup":
          self.$ajaxGet(
            self,
            `BlackRoyalty/dashboard-monthly-by-group?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.monthlyByGroup = response.data || [];
            },
            onFinally
          );
          break;
        case "oem":
          self.$ajaxGet(
            self,
            `BlackRoyalty/oem-vs-consumption?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.oem = response.data || [];
            },
            onFinally
          );
          break;
        case "pitMatrix":
          self.$ajaxGet(
            self,
            `BlackRoyalty/consumption-weekly-matrix?accountId=${self.accountId}&location=Pit&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.pitMatrix = response.data || [];
            },
            onFinally
          );
          break;
        case "dumpMatrix":
          self.$ajaxGet(
            self,
            `BlackRoyalty/consumption-weekly-matrix?accountId=${self.accountId}&location=Dump&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.dumpMatrix = response.data || [];
            },
            onFinally
          );
          break;
        case "weekDetail":
          self.$ajaxGet(
            self,
            `BlackRoyalty/consumption-week-detail?accountId=${self.accountId}&location=${self.weekDetailLocation}&weekNumber=${self.weekDetailWeek}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.weekDetail = response.data || [];
            },
            onFinally
          );
          break;
        case "deliveredKpi":
          self.$ajaxGet(
            self,
            `BlackRoyalty/diesel-delivered-kpi-summary?accountId=${self.accountId}&month=${self.monthParam()}`,
            function (response) {
              self.results.deliveredKpi = response.data ? [response.data] : [];
            },
            onFinally
          );
          break;
        case "deliveredDetail":
          self.$ajaxGet(
            self,
            `BlackRoyalty/diesel-delivered-detail?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`,
            function (response) {
              self.results.deliveredDetail = response.data || [];
            },
            onFinally
          );
          break;
      }
    },
    // The export-all response is a binary .xlsx with Content-Disposition:
    // attachment already set server-side, so a plain navigation triggers the
    // browser's native download - no axios/blob/file-saver plumbing needed.
    exportAll() {
      var self = this;
      self.exporting = true;
      var url = self.$buildUrl(
        `BlackRoyalty/export-all?accountId=${self.accountId}&dieselRate=${self.dieselRate}&month=${self.monthParam()}`
      );
      window.open(url, "_blank");
      self.exporting = false;
    }
  }
};
</script>
